import { Box, Chip, Typography, useTheme } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { DataGrid, DataGridLayout } from '@verantos-dev/shared-components';

import { useGetMandatoryAttributeNamesQuery } from '../../api/createProjectApiSlice';
import { AddedConcept, RequiredAttribute } from '../../state/createProjectSlice';

export const ReviewConceptAndAttributes = ({
  addedConcepts,
}: {
  addedConcepts: AddedConcept[];
}) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const { data: mandatoryAttributeNames } = useGetMandatoryAttributeNamesQuery();
  const manAttrNames = mandatoryAttributeNames?.data ?? [];

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'conceptGroupName',
        headerName: 'Concepts',
        minWidth: 200,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        flex: 1,
      },
      {
        field: 'requiredAttributes',
        headerName: 'Attributes',
        minWidth: 300,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        flex: 2,
        renderCell: (params) => {
          const attributes = params.row.requiredAttributes || [];
          return (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: theme.spacing(1),
                py: theme.spacing(1),
              }}
            >
              {manAttrNames.map((attributeName) => (
                <Chip
                  key={attributeName}
                  label={attributeName}
                  variant="outlined"
                  size="small"
                  sx={{
                    backgroundColor: theme.palette.grey[200],
                    fontSize: '13px',
                    border: 0,
                  }}
                />
              ))}
              {attributes.map((attribute: RequiredAttribute) => (
                <Chip
                  key={attribute.id}
                  label={attribute.label}
                  variant="outlined"
                  size="small"
                  sx={{
                    backgroundColor: theme.palette.grey[200],
                    fontSize: '13px',
                    border: 0,
                  }}
                />
              ))}
            </Box>
          );
        },
      },
    ],
    [theme, manAttrNames]
  );

  const validConcepts = useMemo(() => {
    return addedConcepts.filter((concept) => concept.conceptGroupName.trim());
  }, [addedConcepts]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
      <Typography variant="h6">
        {formatMessage({
          id: 'createProjectWizard.step.reviewAndCreate.conceptAndAttributes.title',
        })}
      </Typography>
      <DataGridLayout id="review-concept-and-attributes-data-grid">
        <DataGrid
          id="review-concept-and-attributes-data-grid"
          mode="client"
          pagination
          initialPageSize={25}
          clearStaleSearchParams
          pageSizeOptions={[10, 25, 50, 100]}
          getRowId={(row) => row.conceptCardId}
          columns={columns}
          rows={validConcepts}
          useFetchDataGridData={() => {
            /* noop */
          }}
          sx={{
            maxHeight: '70vh',
            '& .MuiDataGrid-row': {
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
          }}
        />
      </DataGridLayout>
    </Box>
  );
};
